import React, { useEffect, useRef, useState } from "react";
import { BookMinus, MessageCircle } from "lucide-react"; // Import Lucide's message icon
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { Trash2, View, X } from "lucide-react";

import Select, { SingleValue } from "react-select";
import Flatpickr from "react-flatpickr";

import BreadCrumb from "common/breadcrumb";
import LoadingComponent from "common/components/loader";
import VehicleSearchComponent from "./vehicle-search-component";

import { Service } from "../../models/setup/service";
import { GetProducts, GetPromotions, GetServices, GetServiceTypes } from "api/setup";
import { AssignJob, CreateJob, GenerateInvoice, GetJobDetails, GetJobs, UpdateJob, ViewInvoiceByJobId } from "api/job";

//store
import { useAppDispatch, useAppSelector } from "slices/hook";
import { getCoreDataState } from "slices/core-data/reducer";
import { getAuthState } from "slices/auth/login/reducer";

import { AppUtility } from "utility/app-utility";
import { Customer } from "models/customer/customer";
import { Vehicle } from "models/vehicle/vehicle";
import { LabelValuePair } from "models/label-value";
import { JobStatuses } from "models/settings/branding";
import { AppDispatch } from "slices/store";
import { Job, JobService } from "models/job/job";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IJob, IServiceOption } from "./models";
import { RoutePath } from "routes/route-path";
import { taxesGetThunk } from "slices/core-data/thunk";
import { Invoice } from "models/invoice/invoice";
import { InvoiceSearchParam } from "models/invoice/invoice-search-param";
import { GetInvoices, ResetInvoice, ViewInvoiceId } from "api/invoice";
import { Product } from "models/setup/product";
import { padStart } from "lodash";
import { Promotion } from "models/promotion/promotion";
import { ServiceType } from "models/setup/service-type";
import { User } from "models/user/user";
import { GetTenantUsers } from "api/tenant";
import { JobAssignRequestModel } from "models/job/job-assignment-request";
import { JobAllocationType } from "models/enums";

import { format } from "date-fns-tz";

const JobEditPage: React.FC = () => {
  const title = "Update Job";
  const pageTitle = "Job Management";
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch: AppDispatch = useAppDispatch();
  const { taxes } = useAppSelector(getCoreDataState);

  const [loading, setLoading] = useState(false);
  const [totalFee, setTotalFee] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalPromo, setTotalPromo] = useState(0);

  const [totalServiceFee, setTotalServiceFee] = useState(0);
  const [totalProductPrice, setTotalProductPrice] = useState(0);

  const [edited, setEdited] = useState<Job | undefined>();
  const [selected, setSelected] = useState<IJob | undefined>();
  const [objTobePatched, setobjTobePatched] = useState<Job>({ id: id });
  // const [editMode, setEditMode] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const pageRef = useRef(null);

  const [validation, setValidation] = useState("");
  const [success, setSuccess] = useState("");
  const msgDivRef = useRef(null); // Create a reference for the input field

  const [services, setServices] = useState<Service[] | undefined>([]);
  const [allServiceSelectOptions, setAllServiceSelectOptions] = useState<IServiceOption[]>([]);
  const [serviceSelectOptions, setServiceSelectOptions] = useState<IServiceOption[]>([]);
  const [serviceTypes, setServiceTypes] = useState<LabelValuePair[]>([]);

  const [products, setProducts] = useState<Product[] | undefined>([]);
  const [productSelectOptions, setProductSelectOptions] = useState<LabelValuePair[]>([]);

  const [promotions, setPromotions] = useState<Promotion[] | undefined>([]);

  const [technicians, setTechnician] = useState<LabelValuePair[]>([]);
  const [jobAssignments, setJobAssignments] = useState<JobAssignRequestModel[]>([]);
  const [exitsingAllocations, setExitsingAllocations] = useState<JobAssignRequestModel[]>([]);
  const { loggedInuser } = useAppSelector(getAuthState);

  const [addedServices, setAddedServices] = useState<
    {
      jobServiceId?: string;
      service: LabelValuePair;
      price: string;
      taxRate: number;
      taxAmount: number;
      discount: string;
      discountPercentage?: number;
      discountAmount?: number;
      total: string;
    }[]
  >([]);

  //Products
  const [addedProducts, setAddedProducts] = useState<
    {
      jobProductId?: string;
      product: LabelValuePair;
      price: string;
      taxRate: number;
      taxAmount: number;

      discount: string;
      discountPercentage?: number;
      discountAmount?: number;

      qty: number;
      total: string;
    }[]
  >([]);

  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const timeZone = process.env.REACT_APP_TIMEZONE;

  useEffect(() => {
    const fetchData = async () => {
      dispatch(taxesGetThunk());
      loadInvoices({
        jobId: id ?? "",
      });
      loadServiceTypes();
      await loadServices();
      await loadProducts();
      await loadPromo();
      await loadTechnician();
      loadJob(id ?? "");
    };

    fetchData();
  }, []);

  const loadServiceTypes = async () => {
    const res = await GetServiceTypes();
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.name ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setServiceTypes([{ label: "Any", value: "" }, ..._st]);
    } else {
      setServiceTypes([]);
    }
  };

  useEffect(() => {
    setAddedProducts([]);
    setAddedServices([]);
    setobjTobePatched({ id: id });

    const _mdl: IJob = {
      id: edited?.id ?? undefined,
      description: edited?.description ?? "",
      jobStatus: JobStatuses.find((s) => s.value == (edited?.jobStatusId ?? "DRAFT")),

      insuranceLetterRequired: edited?.insuranceLetterRequired,
      remarks: edited?.remarks ?? "",
      remarksForNextService: edited?.remarksForNextService ?? "",
      warrantyDetails: edited?.warrantyDetails ?? "",
      mileage: edited?.mileage ?? "",
      additionalDiscountAmount: (edited?.additionalDiscountAmount ?? "").toString(),
      promoCode: (edited?.promoCode ?? "").toString(),
      totalPromotionAmount: edited?.totalPromotionAmount ?? 0,
      referrerCode: (edited?.referrerCode ?? "").toString(),
    };
    if ((edited?.customer?.id ?? "") != "") {
      if ((edited?.customer?.vehicles ?? []).length == 0 && edited?.vehicle != null) {
        if (!edited?.customer?.vehicles) {
          edited.customer!.vehicles = [];
        }
        edited.customer!.vehicles.push(edited?.vehicle);
      }
      setSelectedCustomer(edited?.customer);
    } else {
      setSelectedCustomer(undefined);
    }
    setSelected(_mdl);

    if ((edited?.services ?? []).length > 0) {
      const _added: {
        jobServiceId?: string;
        service: LabelValuePair;
        price: string;
        taxRate: number;
        taxAmount: number;
        discount: string;
        discountPercentage?: number;
        discountAmount?: number;
        total: string;
      }[] = [];

      const _addedAllocations: JobAssignRequestModel[] = [];

      (edited?.services ?? []).forEach((itm, index) => {
        const _tax = taxes.find((t) => t.id == itm?.tax?.id);
        const _taxRate =
          (_tax?.rates ?? []).length > 0
            ? (_tax?.rates ?? [])
                .sort((a, b) => {
                  const dateA = new Date(a.effectiveDate!);
                  const dateB = new Date(b.effectiveDate!);

                  // Ensure the dates are valid before comparing
                  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                    return 0; // Return 0 if either date is invalid (to avoid incorrect sorting)
                  }

                  return dateB.getTime() - dateA.getTime(); // Sort in descending order
                })
                .find((t) => t.isActive && new Date(t.effectiveDate!) <= new Date())?.rate ?? 0
            : 0;

        _added.push({
          jobServiceId: itm?.id,
          service: serviceSelectOptions.find((s) => s.value == (itm?.service?.id ?? "")) as LabelValuePair,
          price: (itm?.fee ?? 0.0).toFixed(2).toString(),
          taxRate: _taxRate,
          taxAmount: itm?.taxAmount ?? 0.0,
          discount: (itm?.discountAmount ?? 0).toFixed(2),
          discountAmount: itm?.discountAmount,
          discountPercentage: itm?.discountPercentage,
          total: (itm.totalAmount ?? 0).toFixed(2),
        });

        //alloctaions
        const _dt = new Date();
        _dt.setHours(0);
        _dt.setMinutes(0);
        _dt.setSeconds(0);

        const _existingAlc = (edited?.allocations ?? []).find((a) => a.jobService?.service?.id == itm?.service?.id);
        if (_existingAlc != null) {
          _addedAllocations.push({
            serviceId: (_existingAlc.jobService?.service?.id ?? "").toString(),
            assignedUserId: _existingAlc.assignedEmployee?.id ?? "",
            allocationType: _existingAlc.alloctaionType,
            jobScheduleStart: _existingAlc.jobScheduleStart ?? AppUtility.ISODateToISOString(_dt),
          });
        }
      });
      setAddedServices(_added);
      setExitsingAllocations(_addedAllocations);
    } else {
      setAddedServices([]);
      setExitsingAllocations([]);
    }

    if ((edited?.products ?? []).length > 0) {
      const _added: {
        jobProductId?: string;
        product: LabelValuePair;
        price: string;
        taxRate: number;
        taxAmount: number;
        discount: string;
        qty: number;
        discountAmount: number;
        discountPercentage: number;
        total: string;
      }[] = [];
      (edited?.products ?? []).forEach((itm, index) => {
        const _tax = taxes.find((t) => t.id == itm?.tax?.id);
        const _taxRate =
          (_tax?.rates ?? []).length > 0
            ? (_tax?.rates ?? [])
                .sort((a, b) => {
                  const dateA = new Date(a.effectiveDate!);
                  const dateB = new Date(b.effectiveDate!);

                  // Ensure the dates are valid before comparing
                  if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                    return 0; // Return 0 if either date is invalid (to avoid incorrect sorting)
                  }

                  return dateB.getTime() - dateA.getTime(); // Sort in descending order
                })
                .find((t) => t.isActive && new Date(t.effectiveDate!) <= new Date())?.rate ?? 0
            : 0;

        _added.push({
          jobProductId: itm.id,
          product: productSelectOptions.find((s) => s.value == (itm?.product?.id ?? "")) as LabelValuePair,
          price: (itm?.price ?? 0.0).toFixed(2).toString(),
          taxRate: _taxRate,
          taxAmount: itm?.taxAmount ?? 0.0,
          discount: (itm?.discountAmount ?? 0.0).toFixed(2).toString(),
          qty: itm.quantity ?? 1,
          discountAmount: itm?.discountAmount ?? 0.0,
          discountPercentage: itm?.discountPercentage ?? 0.0,
          total: (itm.totalAmount ?? 0).toFixed(2),
        });
      });
      setAddedProducts(_added);
    } else {
      setAddedProducts([]);
    }
  }, [edited]);

  const formik: any = useFormik({
    initialValues: {
      id: "",
      description: "",
      jobStatus: JobStatuses.find((s) => s.value == "DRAFT"),
      insuranceLetterRequired: false,

      remarks: "",
      remarksForNextService: "",
      warrantyDetails: "",
      mileage: "",
      additionalDiscountAmount: "",
      promoCode: "",
      totalPromotionAmount: 0,
      serviceType: null,
      referrerCode: "",
      ...selected, // Spread the fetched selected  data if available
    },
    validationSchema: Yup.object().shape({
      jobStatus: Yup.object()
        .shape({
          label: Yup.string().required(`Job status is required`),
          value: Yup.string(),
        })
        .required(`Job status must be selected`),
      totalDiscountAmount: AppUtility.formikCurrencyValidtion(),
    }),

    onSubmit: async (values) => {
      try {
        setValidation("");
        /*      if (!selectedCustomer) {
          setValidation("Customer cannot be empty. Please select a customer");
        } else if ((selectedCustomer.vehicles ?? []).length == 0) {
          setValidation(
            "Customer does not have vehicle linked. Please update customer with vehicle details first, or add vehicle for this customer"
          );
        } else */
        {
          if (objTobePatched && Object.keys(objTobePatched).length > 1) {
            setSaving(true);
            focusTop();
            const res = await UpdateJob(objTobePatched);

            if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
              setSuccess("job has been updated successfully");
              setSelected(undefined);

              formik.resetForm();
              setEdited(res?.data ?? undefined);
            } else {
              setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
              loadJob(objTobePatched.id ?? "");
            }

            setTimeout(() => {
              setError("");
              setSuccess("");
            }, 3000);
            setSaving(false);
          }
        }
      } catch (error) {
        setError("Something went wrong...");
        setSaving(false);
        //msgDivRef!.current!.focus()
      }
    },
  });

  useEffect(() => {
    formik.setValues({
      id: selected?.id ?? undefined,
      description: selected?.description ?? "",
      jobStatus: JobStatuses.find((s) => s.value == (selected?.jobStatus?.value ?? "DRAFT")),

      insuranceLetterRequired: selected?.insuranceLetterRequired ?? false,
      remarks: selected?.remarks ?? "",
      remarksForNextService: selected?.remarksForNextService ?? "",
      warrantyDetails: selected?.warrantyDetails ?? "",
      mileage: selected?.mileage ?? "",
      additionalDiscountAmount: (selected?.additionalDiscountAmount ?? "").toString(),
      promoCode: selected?.promoCode ?? "",
      totalPromotionAmount: selected?.totalPromotionAmount ?? 0,
      service: null,
      product: null,
      serviceType: null,
      referrerCode: selected?.referrerCode ?? "",
    });
    setTotalPromo(selected?.totalPromotionAmount ?? 0);
  }, [selected]);

  useEffect(() => {
    const _totalServicePrice = addedServices.reduce(
      (sum, service) => sum + parseFloat(service.price ?? "0") + (service.taxAmount ?? 0.0),
      0
    );
    const _totalServiceDiscount = addedServices.reduce((sum, service) => sum + parseFloat(service.discount ?? "0"), 0);
    setTotalServiceFee(_totalServicePrice - _totalServiceDiscount);

    //products
    const _totalProductPrice = addedProducts.reduce(
      (sum, service) => sum + parseFloat(service.price ?? "0") + (service.taxAmount ?? 0.0),
      0
    );
    const _totalProductDiscount = addedProducts.reduce((sum, service) => sum + parseFloat(service.discount ?? "0"), 0);
    setTotalProductPrice(_totalProductPrice - _totalProductDiscount);

    const _additionalDiscount =
      formik.values.additionalDiscountAmount == "" ? 0 : parseFloat(formik.values.additionalDiscountAmount ?? "0");

    setSubTotal(_totalServicePrice + _totalProductPrice);
    setTotalDiscount(_totalServiceDiscount + _totalProductDiscount + _additionalDiscount);

    const _tot =
      _totalServicePrice - _totalServiceDiscount + (_totalProductPrice - _totalProductDiscount) - _additionalDiscount;

    //Promostions
    let _promoTotal = 0.0;
    if ((formik.values.promoCode ?? "") != "") {
      const _promo = promotions?.find(
        (p) => (p.promoCode ?? "").toLowerCase() == (formik.values.promoCode ?? "").toLowerCase()
      );
      if (_promo != null) {
        let _netTotApplicableForPromo = 0;
        if ((_promo.discountPercentage ?? 0) > 0) {
          if (_promo.servicePromo ?? false) {
            _netTotApplicableForPromo = addedServices.reduce(
              (sum, service) => sum + (parseFloat(service.discount ?? "0") == 0 ? parseFloat(service.price ?? "0") : 0),
              0
            );
          }
          if (_promo.productPromo ?? false) {
            _netTotApplicableForPromo =
              _netTotApplicableForPromo +
              addedProducts.reduce(
                (sum, prd) => sum + (parseFloat(prd.discount ?? "0") == 0 ? parseFloat(prd.price ?? "0") : 0),
                0
              );
          }
          _promoTotal = _netTotApplicableForPromo * 0.01 * (_promo.discountPercentage ?? 0);
        } else {
          _promoTotal = _promo.discountAmount ?? 0;
        }
      }
    }
    setTotalPromo(_promoTotal);
    if (_tot > _promoTotal) {
      setTotalFee(_tot - _promoTotal);
    } else {
      setTotalFee(_tot - _promoTotal);
    }
  }, [formik.values.additionalDiscountAmount, addedServices, addedProducts, formik.values.promoCode]);

  /*   useEffect(() => {
    console.log("Formik values:", formik.values);
  }, [formik.values]); */

  const loadJob = async (id: string) => {
    //param?: TenantSearchParam
    setLoading(true);

    const res = await GetJobDetails(id);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setEdited(res?.data);
    } else {
      setEdited(undefined);
    }
    setLoading(false);
  };

  const loadServices = async () => {
    setLoading(true);
    const param: any = {};
    param["tenantId"] = loggedInuser?.activeTenantId ?? "";

    const res = await GetServices(param);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setServices((res?.data ?? []).flatMap((service, index) => [{ ...service }, ...(service.subServices ?? [])]));
      // Flatten service types for the select input
      const _options = (res?.data ?? []).flatMap((service, index) => [
        {
          isSub: false,
          value: (service.id ?? "").toString(),
          label: `${service.name ?? ""} (${(service.fee ?? 0.0).toFixed(2)})`,
          subOptions: (service.subServices ?? []).map((t, index) => ({
            value: (t.id ?? 0).toString(),
            label: `${t.name ?? ""} (${(t.fee ?? 0.0).toFixed(2)})`,
          })),
          serviceTypeId: (service.serviceType?.id ?? 0).toString(),
        },
        ...(service.subServices ?? []).map((sub) => ({
          isSub: true,
          value: (sub.id ?? "").toString(),
          label: `   ${sub.name ?? ""} (${(sub.fee ?? 0.0).toFixed(2)})`, // Indent the label for display purposes
          serviceTypeId: (service.serviceType?.id ?? 0).toString(),
        })),
      ]);

      setServiceSelectOptions(_options);
      setAllServiceSelectOptions([..._options]);
    } else {
      setServices([]);
      setServiceSelectOptions([]);
      setAllServiceSelectOptions([]);
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    setLoading(true);
    const param: any = {};
    // param["tenantId"] = loggedInuser?.activeTenantId ?? "";

    const res = await GetProducts(param);
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({
        value: (t.id ?? 0).toString(),
        label: `${t.name ?? ""} (${(t.price ?? 0.0).toFixed(2)})`,
      }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setProducts(res?.data ?? []);
      setProductSelectOptions(_st);
    } else {
      setProducts([]);
      setProductSelectOptions([]);
    }
    setLoading(false);
  };

  const loadInvoices = async (param: InvoiceSearchParam) => {
    const res = await GetInvoices(param, undefined, 10, 1);
    if (!(res?.status?.isError ?? false) && res?.data && res?.data?.length > 0) {
      setInvoices(res?.data);
    }
  };

  //Customer
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const onCustomerSelect = (cus?: Customer) => {
    setSelectedCustomer(cus);
    if (cus) {
      const _object: Job = { ...objTobePatched, customer: {}, vehicle: {} };
      _object.customer!.id = cus?.id;
      if ((cus.vehicles ?? []).length > 0) {
        _object.vehicle!.id = cus!.vehicles![0].id;
      }
      setobjTobePatched(_object);
    }
  };
  //Services
  // Custom format option label to indent sub-options
  const formatOptionLabel = (option: IServiceOption) => (
    <div>
      <div>{option.label}</div>
      {option.subOptions &&
        option.subOptions.map((sub) => (
          <div key={sub.value} className="ml-4 text-gray-600">
            {sub.label}
          </div>
        ))}
    </div>
  );

  const handleAddService = (value: IServiceOption) => {
    const _exists = addedServices.find((s) => (s.service?.value ?? "") == value.value);
    if (!_exists) {
      const _selected = services?.find((s) => (s.id ?? "") == value.value);
      const _tax = taxes.find((t) => t.id == _selected?.tax?.id);
      const _taxRate =
        (_tax?.rates ?? []).length > 0
          ? (_tax?.rates ?? [])
              .sort((a, b) => {
                const dateA = new Date(a.effectiveDate!);
                const dateB = new Date(b.effectiveDate!);

                // Ensure the dates are valid before comparing
                if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                  return 0; // Return 0 if either date is invalid (to avoid incorrect sorting)
                }

                return dateB.getTime() - dateA.getTime(); // Sort in descending order
              })
              .find((t) => t.isActive && new Date(t.effectiveDate!) <= new Date())?.rate ?? 0
          : 0;
      let _netTot = _selected?.fee ?? 0.0;
      let _disTot =
        (_selected?.discountPercentage ?? 0.0) != 0
          ? _netTot * (_selected?.discountPercentage ?? 0.0) * 0.01
          : _selected?.discountAmount ?? 0.0;
      let _taxTot = (_netTot - _disTot) * (_taxRate / 100);

      setAddedServices([
        ...addedServices,
        {
          service: value,
          price: (_selected?.fee ?? 0.0).toFixed(2).toString(),
          taxRate: _taxRate,
          taxAmount: _taxTot,
          discountAmount: _selected?.discountAmount,
          discountPercentage: _selected?.discountPercentage,
          discount: _disTot.toFixed(2).toString(),
          total: (_netTot + _taxTot - _disTot).toFixed(2).toString(),
        },
      ]);
      setobjTobePatched({
        ...objTobePatched,
        services: [
          ...(objTobePatched?.services ?? []),
          {
            service: {
              id: parseFloat(value?.value ?? "0"),
              name: value?.label,
            },
            fee: _selected?.fee ?? 0.0,
            discountAmount: _disTot,
          },
        ],
      });
    }
  };

  const handleRemoveService = (index: number) => {
    const _service = addedServices[index];
    if ((_service?.service?.value ?? "") != "") {
      //setRemovedServices([...removedServices,_service.service])
      //update patch object
      const _patchServices = [...(objTobePatched?.services ?? [])];
      const _svc = _patchServices.find((s) => (s.service!.id ?? "") == _service.service?.value);
      if (_svc) {
        _svc.deleted = true;
      } else {
        _patchServices.push({
          id: _service.jobServiceId,
          deleted: true,
          service: { id: parseFloat(_service.service?.value ?? "0"), name: _service.service?.label },
        });
      }
      setobjTobePatched({
        ...objTobePatched,
        services: _patchServices,
      });
    }

    const newServices = addedServices.filter((_, i) => i !== index);
    setAddedServices([...newServices]);
  };

  //Products
  const handleAddProduct = (value: LabelValuePair) => {
    const _exists = addedProducts.find((s) => (s.product?.value ?? "") == value.value);
    if (!_exists) {
      const _selected = products?.find((s) => (s.id ?? "") == value.value);
      const _tax = taxes.find((t) => t.id == _selected?.tax?.id);
      const _taxRate =
        (_tax?.rates ?? []).length > 0
          ? (_tax?.rates ?? [])
              .sort((a, b) => {
                const dateA = new Date(a.effectiveDate!);
                const dateB = new Date(b.effectiveDate!);

                // Ensure the dates are valid before comparing
                if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                  return 0; // Return 0 if either date is invalid (to avoid incorrect sorting)
                }

                return dateB.getTime() - dateA.getTime(); // Sort in descending order
              })
              .find((t) => t.isActive && new Date(t.effectiveDate!) <= new Date())?.rate ?? 0
          : 0;
      let _netTot = _selected?.price ?? 0.0;
      let _disTot =
        (_selected?.discountPercentage ?? 0.0) != 0
          ? _netTot * (_selected?.discountPercentage ?? 0.0) * 0.01
          : _selected?.discountAmount ?? 0.0;

      let _taxTot = (_netTot - _disTot) * (_taxRate / 100);

      setAddedProducts([
        ...addedProducts,
        {
          product: value,
          price: _netTot.toFixed(2).toString(),
          qty: 1,
          taxRate: _taxRate,
          taxAmount: _taxTot,
          discount: _disTot.toFixed(2).toString(),
          discountPercentage: _selected?.discountPercentage,
          discountAmount: _selected?.discountAmount,
          total: (_netTot + _taxTot - _disTot).toFixed(2).toString(),
        },
      ]);

      setobjTobePatched({
        ...objTobePatched,
        products: [
          ...(objTobePatched?.products ?? []),
          {
            product: {
              id: parseFloat(value?.value ?? "0"),
              name: value?.label,
            },
            quantity: 1,
            price: _selected?.price ?? 0.0,
            discountAmount: _disTot,
          },
        ],
      });
    }
  };

  const handleRemoveProduct = (index: number) => {
    const _product = addedProducts[index];
    if ((_product?.product?.value ?? "") != "") {
      const _patchProducts = [...(objTobePatched?.products ?? [])];
      const _svc = _patchProducts.find((s) => (s.product!.id ?? "") == _product.product?.value);
      if (_svc) {
        _svc.deleted = true;
      } else {
        _patchProducts.push({
          id: _product.jobProductId,
          deleted: true,
          product: { id: parseFloat(_product.product?.value ?? "0"), name: _product.product?.label },
        });
      }
      setobjTobePatched({
        ...objTobePatched,
        products: _patchProducts,
      });
    }

    const newServices = addedProducts.filter((_, i) => i !== index);
    setAddedProducts([...newServices]);
  };

  //Promotions
  const loadPromo = async () => {
    setLoading(true);
    const param: any = {};
    // param["tenantId"] = loggedInuser?.activeTenantId ?? "";

    const res = await GetPromotions(param);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setPromotions(res?.data ?? []);
    } else {
      setPromotions([]);
    }
    setLoading(false);
  };

  //Technician
  const loadTechnician = async () => {
    // setLoading(true);
    const param: any = {};

    const res = await GetTenantUsers(loggedInuser?.activeTenantId ?? "", param, undefined, 10000, 1);
    if (!(res?.status?.isError ?? false) && res?.data) {
      let _st = (res?.data ?? []).map((t, index) => ({ value: (t.id ?? 0).toString(), label: t.firstName ?? "" }));
      if (_st) {
        _st = _st.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
      }
      setTechnician([{ label: "Not set", value: "" }, ..._st]);
    } else {
      setTechnician([]);
    }
    //setLoading(false);
  };

  //form reset
  const handleResetClick = () => {
    setEdited({ ...edited });
  };

  const onInvoiceCreate = async () => {
    setSaving(true);

    const res = await GenerateInvoice(selected?.id ?? "0");

    if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
      setSuccess("Invoice has been generated successfully");
      onViewInvoiceByJobId();
      loadInvoices({ jobId: id ?? "" });
    } else {
      setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    setSaving(false);
  };
  const focusTop = async () => {
    console.log("focus top");
    setTimeout(() => {
      window.scrollTo(0, 0);
      /*   if (pageRef && pageRef.current) {
        (pageRef.current as any).focus();
      } */
    }, 100);
  };

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const onViewInvoiceByJobId = async () => {
    setSaving(true);
    focusTop();
    const blob = await ViewInvoiceByJobId(id ?? "0");

    if (blob && blob.size > 0) {
      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);

      // Open the Blob URL in a new tab
      window.open(url, "_blank");
      let _dt = new Date();
      // Create the download link
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download =
        (edited?.vehicle?.vehicleNo ?? "").replace(" ", "_") +
        "_" +
        padStart(_dt.getDate().toString(), 2) +
        "_" +
        months[_dt.getMonth()] +
        "_" +
        _dt.getFullYear();

      // Trigger the download
      downloadLink.click();

      // Revoke the object URL
      URL.revokeObjectURL(url);
      //setSuccess("Invoice has been generated successfully");
    } else if (blob && blob.size == 0) {
      setError(`error: Invoice not found`);
    } else {
      setError(`error: in viewing invoice`);
    }

    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    setSaving(false);
  };
  const onResetInvoice = async (invoiceId: string) => {
    setSaving(true);
    focusTop();
    const res = await ResetInvoice(invoiceId ?? "0");
    if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
      setSuccess("Invoice has been reset successfully");
      loadInvoices({
        jobId: id ?? "",
      });
    } else {
      setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    setSaving(false);
  };

  const onViewInvoice = async (invoiceId: string) => {
    setSaving(true);
    focusTop();
    const blob = await ViewInvoiceId(invoiceId ?? "0");

    if (blob && blob.size > 0) {
      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);

      // Open the Blob URL in a new tab
      window.open(url, "_blank");

      //setSuccess("Invoice has been generated successfully");
    } else if (blob && blob.size == 0) {
      setError(`error: Invoice not found`);
    } else {
      setError(`error: in viewing invoice`);
    }
    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    setSaving(false);
  };

  const onInvoicePay = async () => {
    var _job: Job = { id: id, jobStatusId: "PAID" };
    setSaving(true);
    focusTop();
    const res = await UpdateJob(_job);

    if (!(res?.status?.isError ?? false) && (res?.data?.id ?? "") != "") {
      setSuccess("job has been marked as paid successfully");
      setSelected(undefined);

      formik.resetForm();
      setEdited(res?.data ?? undefined);
    } else {
      setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      // loadJob(objTobePatched.id ?? "");
    }

    setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    setSaving(false);
  };
  const onAssignClick = async () => {
    if ((jobAssignments ?? []).length > 0) {
      setSaving(true);
      focusTop();
      const res = await AssignJob(edited?.id ?? "", jobAssignments ?? []);

      if (!(res?.status?.isError ?? false) && (res?.data ?? []).length > 0) {
        setJobAssignments([]);
        loadJob(id ?? "");
        setSuccess("job has been assigned successfully");
      } else {
        setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
      }

      setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
      setSaving(false);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div
        ref={pageRef}
        tabIndex={0} // Makes the div focusable
      ></div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
        <div className="xl:col-span-12">
          <div className="card">
            <div className="card-body">
              {/*  <h6 className="mb-4 text-15"> {(selected?.id ?? "") == "" ? "Create" : "Update"} Job</h6> */}

              {loading && <LoadingComponent />}
              {saving && <LoadingComponent />}
              <div ref={msgDivRef}></div>
              {success && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                  id="successAlert"
                >
                  <b>Success! </b>
                  {success}
                </div>
              )}
              {validation && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                  id="successAlert"
                >
                  <>{validation}</>
                </div>
              )}
              {error && (
                <div
                  ref={errorRef} // Referencing the div
                  tabIndex={-1} // Makes the div focusable
                >
                  <div
                    className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                    id="successAlert"
                  >
                    <>
                      {error}
                      {/*   Oops! Something went wrong while attempting to update job. Please try again later or contact support
                    for assistance. */}
                    </>
                  </div>
                </div>
              )}

              <form
                action="#!"
                id="jobForm"
                onSubmit={(event: any) => {
                  event.preventDefault();
                  formik.handleSubmit();
                }}
              >
                <>
                  <div className="grid grid-cols-1 gap-x-5">
                    <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                      <div className="mb-4 xl:col-span-8">
                        <VehicleSearchComponent
                          onCustomerSelect={onCustomerSelect}
                          defaultCustomer={selectedCustomer}
                        ></VehicleSearchComponent>
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <div className="flex justify-end gap-2 mt-5">
                          <div>
                            <a
                              href="#"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-500 hover:text-green-700"
                              onClick={(e) => {
                                e.preventDefault();
                                let phone = selectedCustomer?.contactNumber ?? "";
                                if (!phone.startsWith("+")) {
                                  phone = "+94" + phone.slice(-9);
                                }
                                const whatsappUrl = `https://web.whatsapp.com/send?phone=${phone}`;
                                window.open(whatsappUrl, "_blank");
                              }}
                            >
                              <MessageCircle size={40} />
                            </a>
                          </div>
                          <button
                            type="button"
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white 
                      hover:bg-custom-600 hover:border-custom-600 
                      focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                      active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                      dark:ring-custom-400/20
                      px-4
                      "
                            onClick={onInvoiceCreate}
                          >
                            {(invoices ?? []).length > 0 ? "Update Invoice" : "Create Invoice"}
                          </button>
                          {(invoices ?? []).length > 0 && (
                            <>
                              <button
                                type="button"
                                className="text-white btn bg-custom-500 border-custom-500 hover:text-white 
                      hover:bg-custom-600 hover:border-custom-600 
                      focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                      active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                      dark:ring-custom-400/20
                      px-4
                      "
                                onClick={onViewInvoiceByJobId}
                              >
                                View Invoice
                              </button>
                              <button
                                type="button"
                                className="text-white btn bg-custom-500 border-custom-500 hover:text-white 
                      hover:bg-custom-600 hover:border-custom-600 
                      focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                      active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                      dark:ring-custom-400/20
                      px-4
                      "
                                onClick={() => {
                                  // navigate(`${RoutePath.Admin_Job_invoice_preview.replace("/:id", "/" + selected?.id)}`);
                                  const url = `#${RoutePath.Admin_Job_invoice_preview.replace("/:id", "/" + id)}`;
                                  window.open(url, "_blank"); // Open the route in a new tab
                                }}
                              >
                                Preview
                              </button>
                            </>
                          )}
                          {(invoices ?? []).length > 0 &&
                            edited?.jobStatusId != "PAID" &&
                            edited?.jobStatusId != "COMPLETED" &&
                            edited?.jobStatusId != "CANCELLED" && (
                              <button
                                type="button"
                                className="text-white btn bg-custom-500 border-custom-500 hover:text-white 
                        hover:bg-custom-600 hover:border-custom-600 
                        focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                        active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                        dark:ring-custom-400/20
                        px-8
                        "
                                onClick={onInvoicePay}
                              >
                                Marked as Paid
                              </button>
                            )}
                        </div>
                      </div>
                      <div className="mb-4 xl:col-span-12">
                        <label htmlFor="description" className="inline-block mb-2 text-base font-bold">
                          Description{/*  <span className="text-red-500">*</span> */}
                        </label>
                        <textarea
                          name="description"
                          className="form-textarea w-full rounded-lg border border-gray-300 p-4
                          border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Type your job description here"
                          value={formik.values.description}
                          //onChange={formik.handleChange}
                          onChange={(e) => {
                            formik.setFieldValue("description", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, description: e.target?.value ?? "" });
                          }}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                        {formik.touched.description && formik.errors.description ? (
                          <p id="error_description" className="mt-1 text-sm text-red-500">
                            {formik.errors.description}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="jobStatus" className="inline-block mb-2 text-base font-bold">
                          Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                          className="border-slate-200 focus:outline-none focus:border-custom-500"
                          options={JobStatuses}
                          isSearchable={false} // To disable search
                          name="jobStatus"
                          id="jobStatus"
                          value={formik.values.jobStatus} // Set Formik's `serviceType` value
                          onChange={(option) => {
                            setobjTobePatched({ ...objTobePatched, jobStatusId: option.value });
                            formik.setFieldValue("jobStatus", option);
                          }} // Update Formik state
                        />
                        {formik.touched.jobStatus && formik.errors.jobStatus ? (
                          <p id="error_jobStatus" className="mt-1 text-sm text-red-500">
                            {formik.errors.jobStatus.label || formik.errors.jobStatus}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="mileage" className="inline-block mb-2 text-base font-bold">
                          Mileage
                        </label>
                        <input
                          type="text"
                          name="mileage"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Mileage"
                          value={formik.values.mileage}
                          onChange={(e) => {
                            formik.setFieldValue("mileage", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, mileage: e.target?.value ?? "" });
                          }}
                          // Update Formik state
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.mileage && formik.errors.mileage ? (
                          <p id="error_mileage" className="mt-1 text-sm text-red-500">
                            {formik.errors.mileage}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="additionalDiscountAmount" className="inline-block mb-2 text-base font-bold">
                          Additional Discount
                        </label>
                        <input
                          type="text"
                          name="additionalDiscountAmount"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Discount"
                          value={formik.values.additionalDiscountAmount}
                          //onChange={formik.handleChange}
                          onChange={(e) => {
                            if (e.target.value == "") {
                              formik.setFieldValue("additionalDiscountAmount", "0.00");
                            } else {
                              formik.setFieldValue("additionalDiscountAmount", parseFloat(e.target.value));
                            }
                            setobjTobePatched({
                              ...objTobePatched,
                              additionalDiscountAmount: parseFloat(e.target?.value ?? "0"),
                            });
                          }}
                          // Update Formik state
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.additionalDiscountAmount && formik.errors.additionalDiscountAmount ? (
                          <p id="error_additionalDiscountAmount" className="mt-1 text-sm text-red-500">
                            {formik.errors.additionalDiscountAmount}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-4" style={{ display: "none" }}>
                        <label htmlFor="insuranceLetterRequired" className="inline-block mb-2 text-base font-bold">
                          Insurance Letter Required
                        </label>
                        <div className="flex items-center">
                          <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                            <input
                              type="checkbox"
                              name="insuranceLetterRequired"
                              id="insuranceLetterRequired"
                              className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                              onChange={(event) => {
                                formik.setFieldValue("insuranceLetterRequired", event.target.checked);
                                setobjTobePatched({
                                  ...objTobePatched,
                                  insuranceLetterRequired: event.target?.checked ?? false,
                                });
                              }}
                              checked={formik.values.insuranceLetterRequired}
                            />
                            <label
                              htmlFor="insuranceLetterRequired"
                              className="block h-5 overflow-hidden font-bold
                              duration-300 ease-linear border rounded-full 
                              cursor-pointer cursor-pointertransition 
                              border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 
                              peer-checked/published:border-green-500"
                            ></label>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="promoCode" className="inline-block mb-2 text-base font-bold">
                          Promo Code
                        </label>
                        <input
                          type="text"
                          name="promoCode"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Promo Code"
                          value={formik.values.promoCode}
                          onChange={(e) => {
                            formik.setFieldValue("promoCode", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, promoCode: e.target?.value ?? "" });
                          }}
                          // Update Formik state
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.promoCode && formik.errors.promoCode ? (
                          <p id="error_promoCode" className="mt-1 text-sm text-red-500">
                            {formik.errors.promoCode}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-4">
                        <label htmlFor="referrerCode" className="inline-block mb-2 text-base font-bold">
                          Referrer Code
                        </label>
                        <input
                          type="text"
                          name="referrerCode"
                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Referrer Code"
                          value={formik.values.referrerCode}
                          onChange={(e) => {
                            formik.setFieldValue("referrerCode", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, referrerCode: e.target?.value ?? "" });
                          }}
                          // Update Formik state
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.referrerCode && formik.errors.referrerCode ? (
                          <p id="error_referrerCode" className="mt-1 text-sm text-red-500">
                            {formik.errors.referrerCode}
                          </p>
                        ) : null}
                      </div>

                      <div className="mb-4 xl:col-span-12">
                        <label htmlFor="remarks" className="inline-block mb-2 text-base font-bold">
                          Remarks{/*  <span className="text-red-500">*</span> */}
                        </label>
                        <textarea
                          name="remarks"
                          className="form-textarea w-full rounded-lg border border-gray-300 p-4
                          border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Add remarks here"
                          value={formik.values.remarks}
                          onChange={(e) => {
                            formik.setFieldValue("remarks", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, remarks: e.target?.value ?? "" });
                          }}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                        {formik.touched.remarks && formik.errors.remarks ? (
                          <p id="error_remarks" className="mt-1 text-sm text-red-500">
                            {formik.errors.remarks}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-12">
                        <label htmlFor="remarksForNextService" className="inline-block mb-2 text-base font-bold">
                          Remarks for Next Service{/*  <span className="text-red-500">*</span> */}
                        </label>
                        <textarea
                          name="remarksForNextService"
                          className="form-textarea w-full rounded-lg border border-gray-300 p-4
                          border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Add remarks here"
                          value={formik.values.remarksForNextService}
                          onChange={(e) => {
                            formik.setFieldValue("remarksForNextService", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, remarksForNextService: e.target?.value ?? "" });
                          }}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                        {formik.touched.remarksForNextService && formik.errors.remarksForNextService ? (
                          <p id="error_remarksForNextService" className="mt-1 text-sm text-red-500">
                            {formik.errors.remarksForNextService}
                          </p>
                        ) : null}
                      </div>
                      <div className="mb-4 xl:col-span-12">
                        <label htmlFor="warrantyDetails" className="inline-block mb-2 text-base font-bold">
                          Warranty Details{/*  <span className="text-red-500">*</span> */}
                        </label>
                        <textarea
                          name="warrantyDetails"
                          className="form-textarea w-full rounded-lg border border-gray-300 p-4
                          border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                          placeholder="Add warranty details here"
                          value={formik.values.warrantyDetails}
                          onChange={(e) => {
                            formik.setFieldValue("warrantyDetails", e.target?.value ?? "");
                            setobjTobePatched({ ...objTobePatched, warrantyDetails: e.target?.value ?? "" });
                          }}
                          onBlur={formik.handleBlur}
                          rows={3}
                        />
                        {formik.touched.warrantyDetails && formik.errors.warrantyDetails ? (
                          <p id="error_warrantyDetails" className="mt-1 text-sm text-red-500">
                            {formik.errors.warrantyDetails}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-gray-300 p-4 shadow-md">
                    <div className="grid grid-cols-1 gap-x-5">
                      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                        <div className="xl:col-span-12">
                          <label htmlFor="service" className="inline-block mb-4 text-base font-bold">
                            Job Services
                          </label>
                        </div>
                        <div className="xl:col-span-5">
                          <Select
                            className="border-slate-200 focus:outline-none focus:border-custom-500 w-full"
                            options={serviceTypes}
                            isSearchable={true} // To disable search
                            name="serviceType"
                            id="serviceType"
                            /*   formatOptionLabel={formatOptionLabel} */
                            value={formik.values.serviceType} // Set Formik's `serviceType` value
                            onChange={(option: LabelValuePair) => {
                              formik.setFieldValue("serviceType", option);
                              if ((option?.value ?? "") == "") {
                                setServiceSelectOptions([...allServiceSelectOptions]);
                              } else {
                                const _options = allServiceSelectOptions.filter(
                                  (s) => s.serviceTypeId == (option?.value ?? "")
                                );
                                setServiceSelectOptions([..._options]);
                              }
                            }} // Update Formik state
                          />
                        </div>
                        <div className="xl:col-span-5">
                          <div className="flex flex-row">
                            <Select
                              className="border-slate-200 focus:outline-none focus:border-custom-500 w-full"
                              options={serviceSelectOptions}
                              isSearchable={true} // To disable search
                              name="service"
                              id="service"
                              formatOptionLabel={(option) => (
                                <div
                                  className={option.isSub ? "sub-option-class" : ""}
                                  style={{ paddingLeft: option.isSub ? "20px" : "0px" }}
                                >
                                  {option.label}
                                </div>
                              )}
                              /*   formatOptionLabel={formatOptionLabel} */
                              value={formik.values.service} // Set Formik's `serviceType` value
                              onChange={(option) => handleAddService(option)} // Update Formik state
                            />
                          </div>
                        </div>
                        {/*  <div className="xl:col-span-6">
                          <button
                            type="button"
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                          >
                            Add Service
                          </button>
                        </div> */}
                      </div>
                    </div>
                    {/* Added Services Section */}
                    <div className="grid grid-cols-1 gap-x-1 mt-4 text-sm font-medium">
                      {addedServices.map((service, index) => (
                        <div key={index} className="grid grid-cols-1 lg:grid-cols-12  gap-2 mb-2 items-center">
                          <span className="lg:col-span-3">{service.service?.label ?? ""}</span>
                          <div className="lg:col-span-2 flex flex-row">
                            <label htmlFor={`price_${index}`} className="inline-block m-4 text-sm w-[40%]">
                              Fee
                            </label>
                            <input
                              name={`price_${index}`}
                              type="text"
                              placeholder="Price"
                              value={service.price}
                              onChange={(e) => {
                                //setTimeout(() => {
                                const updatedServices = [...addedServices];
                                updatedServices[index].price = (
                                  e.target.value === "" ? 0.0 : parseFloat(e.target.value)
                                )
                                  //   .toFixed(2)
                                  .toString();

                                let _netTot = e.target.value === "" ? 0.0 : parseFloat(e.target.value);

                                let _disTot =
                                  (updatedServices[index]?.discountPercentage ?? 0.0) != 0
                                    ? _netTot * (updatedServices[index]?.discountPercentage ?? 0.0) * 0.01
                                    : (updatedServices[index]?.discount ?? "") == ""
                                    ? 0.0
                                    : parseFloat(updatedServices[index]?.discount);
                                let _taxTot = (_netTot - _disTot) * (updatedServices[index].taxRate / 100);

                                updatedServices[index].taxAmount = _taxTot;
                                (updatedServices[index].discount = _disTot.toFixed(2).toString()),
                                  (updatedServices[index].total = (_netTot + _taxTot - _disTot).toFixed(2).toString()),
                                  setAddedServices(updatedServices);

                                //update patch object
                                const _patchServices = [...(objTobePatched?.services ?? [])];
                                const _svc = _patchServices.find(
                                  (s) => (s.service!.id ?? "") == updatedServices[index]!.service!.value
                                );
                                if (_svc) {
                                  _svc.fee = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                } else {
                                  _patchServices.push({
                                    service: {
                                      id: parseFloat(updatedServices[index]?.service.value ?? "0"),
                                      name: updatedServices[index]?.service?.label,
                                    },
                                    fee: parseFloat(updatedServices[index]?.price ?? 0.0),
                                    discountAmount: parseFloat(updatedServices[index]?.discount ?? 0.0),
                                  });
                                }
                                setobjTobePatched({
                                  ...objTobePatched,
                                  services: _patchServices,
                                });
                                // }, 1000);
                              }}
                              className="border rounded p-1 text-right w-full"
                            />
                            {/*   <label htmlFor={`price_tax_${index}`} className="inline-block m-4 text-base font-medium">
                              Tax : {service.taxAmount.toFixed(2)}
                            </label> */}
                          </div>
                          <div className="lg:col-span-2 flex flex-row">
                            <label htmlFor={`discount_${index}`} className="inline-block m-4 text-sm w-[40%]">
                              Discount
                            </label>
                            <input
                              name={`discount_${index}`}
                              type="text"
                              placeholder="Discount"
                              value={service.discount}
                              disabled={(service.discountPercentage ?? 0) != 0}
                              onChange={(e) => {
                                if ((service.discountPercentage ?? 0) == 0) {
                                  const updatedServices = [...addedServices];
                                  updatedServices[index].discount = (
                                    e.target.value === "" ? 0.0 : parseFloat(e.target.value)
                                  )
                                    // .toFixed(2)
                                    .toString();
                                  let _netTot = parseFloat(updatedServices[index].price);
                                  let _disTot = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                  let _taxTot = (_netTot - _disTot) * (updatedServices[index].taxRate / 100);

                                  updatedServices[index].taxAmount = _taxTot;
                                  // (updatedServices[index].discount = _disTot.toFixed(2).toString()),
                                  (updatedServices[index].total = (_netTot + _taxTot - _disTot).toFixed(2).toString()),
                                    setAddedServices(updatedServices);

                                  //update patch object
                                  const _patchServices = [...(objTobePatched?.services ?? [])];
                                  const _svc = _patchServices.find(
                                    (s) => (s.service!.id ?? "") == updatedServices[index]!.service!.value
                                  );
                                  if (_svc) {
                                    _svc.discountAmount = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                  } else {
                                    _patchServices.push({
                                      service: {
                                        id: parseFloat(updatedServices[index]?.service.value ?? "0"),
                                        name: updatedServices[index]?.service?.label,
                                      },
                                      fee: parseFloat(updatedServices[index]?.price ?? 0.0),
                                      discountAmount: parseFloat(updatedServices[index]?.discount ?? 0.0),
                                    });
                                  }
                                  setobjTobePatched({
                                    ...objTobePatched,
                                    services: _patchServices,
                                  });
                                }
                              }}
                              className="border rounded p-1 text-sm text-right w-full"
                            />
                          </div>

                          <div className="lg:col-span-2">
                            <label htmlFor={`price_tax_${index}`} className="inline-block m-4 text-sm">
                              Tax : {(service.taxAmount ?? 0).toFixed(2)}
                            </label>
                          </div>
                          <div className="lg:col-span-2">
                            <label htmlFor={`price_total_${index}`} className="inline-block m-4 text-sm">
                              Total : {service.total ?? "0.00"}
                            </label>
                          </div>

                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleRemoveService(index)}
                              className="text-red-500 md:col-span-1"
                            >
                              <div className="flex flex-row">
                                <Trash2 />
                                {/*  <span className="p-1">Remove</span> */}
                              </div>
                            </button>
                          </div>
                          {/*  Line separator visible only on md and smaller screens  For large screens and larger ( lg, xl), the line separator will be hidden. */}
                          {addedServices.length - 1 != index && (
                            <div className="lg:hidden border-t border-gray-300 my-4"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-5 font-bold">
                    Total Service Fee: {AppUtility.FormattedPrice(totalServiceFee)}
                  </div>

                  {/*  Products */}
                  <div className="rounded-lg border border-gray-300 p-4 shadow-md">
                    <div className="grid grid-cols-1 gap-x-5">
                      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                        <div className="xl:col-span-12">
                          <label htmlFor="product" className="inline-block mb-4 text-base font-bold">
                            Products/Parts
                          </label>
                        </div>
                        <div className="xl:col-span-10">
                          <Select
                            className="border-slate-200 focus:outline-none focus:border-custom-500"
                            options={productSelectOptions}
                            isSearchable={true} // To disable search
                            name="product"
                            id="product"
                            /*   formatOptionLabel={formatOptionLabel} */
                            value={formik.values.product} // Set Formik value
                            onChange={(option) => handleAddProduct(option)} // Update Formik state
                          />
                        </div>
                        {/*  <div className="xl:col-span-6">
                          <button
                            type="button"
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                          >
                            Add Service
                          </button>
                        </div> */}
                      </div>
                    </div>
                    {/* Added Products Section */}
                    <div className="grid grid-cols-1 gap-x-1 mt-4 text-sm font-medium">
                      {addedProducts.map((product, index) => (
                        <div key={index} className="grid grid-cols-1 lg:grid-cols-12  gap-2 mb-2 items-center">
                          <span className="lg:col-span-3">{product.product?.label ?? ""}</span>
                          <div className="lg:col-span-2 flex flex-row">
                            <label htmlFor={`p_qty_${index}`} className="inline-block m-4 text-sm w-[40%]">
                              Quantity
                            </label>
                            <input
                              name={`p_qty_${index}`}
                              type="number"
                              placeholder="Quantity"
                              min={1}
                              value={product.qty}
                              onChange={(e) => {
                                const updatedProducts = [...addedProducts];

                                let _unitPrice = parseFloat(updatedProducts[index].price) / updatedProducts[index].qty;
                                let _unitDiscount =
                                  parseFloat(updatedProducts[index].discount ?? 0) / updatedProducts[index].qty;
                                updatedProducts[index].qty = e.target.value == "" ? 1 : parseFloat(e.target.value);

                                let _netTot = _unitPrice * updatedProducts[index].qty;
                                let _disTot =
                                  (updatedProducts[index]?.discountPercentage ?? 0.0) != 0
                                    ? _netTot * (updatedProducts[index]?.discountPercentage ?? 0.0) * 0.01
                                    : (updatedProducts[index]?.discount ?? "") == ""
                                    ? 0.0
                                    : _unitDiscount * updatedProducts[index].qty;

                                let _taxTot = (_netTot - _disTot) * (updatedProducts[index].taxRate / 100);

                                updatedProducts[index].price = _netTot.toFixed(2);
                                updatedProducts[index].taxAmount = _taxTot;
                                updatedProducts[index].discount = _disTot.toFixed(2);
                                updatedProducts[index].total = (_netTot - _disTot + _taxTot).toFixed(2);
                                setAddedProducts(updatedProducts);

                                //update patch object
                                const _patchProducts = [...(objTobePatched?.products ?? [])];
                                const _svc = _patchProducts.find(
                                  (s) => (s.product!.id ?? "") == updatedProducts[index]!.product!.value
                                );
                                if (_svc) {
                                  _svc.quantity = updatedProducts[index].qty;
                                  _svc.discountAmount = _disTot;
                                } else {
                                  _patchProducts.push({
                                    product: {
                                      id: parseFloat(updatedProducts[index]?.product.value ?? "0"),
                                      name: updatedProducts[index]?.product?.label,
                                    },
                                    quantity: updatedProducts[index].qty,
                                    price: parseFloat(updatedProducts[index]?.price ?? 0.0),
                                    discountAmount: parseFloat(updatedProducts[index]?.discount ?? 0.0),
                                  });
                                }
                                setobjTobePatched({
                                  ...objTobePatched,
                                  products: _patchProducts,
                                });
                              }}
                              className="border rounded p-1 text-right w-full"
                            />
                          </div>

                          <div className="lg:col-span-2 flex flex-row">
                            <label htmlFor={`p_price_${index}`} className="inline-block m-4 text-sm w-[40%]">
                              Price
                            </label>
                            <input
                              name={`p_price_${index}`}
                              type="text"
                              placeholder="Price"
                              value={product.price}
                              onChange={(e) => {
                                const updatedProducts = [...addedProducts];
                                updatedProducts[index].price = (
                                  e.target.value === "" ? 0.0 : parseFloat(e.target.value)
                                )
                                  //  .toFixed(2)
                                  .toString();

                                let _netTot = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                let _disTot =
                                  (updatedProducts[index]?.discountPercentage ?? 0.0) != 0
                                    ? _netTot * (updatedProducts[index]?.discountPercentage ?? 0.0) * 0.01
                                    : (updatedProducts[index]?.discount ?? "") == ""
                                    ? 0.0
                                    : parseFloat(updatedProducts[index]?.discount);

                                let _taxTot = (_netTot - _disTot) * (updatedProducts[index].taxRate / 100);

                                updatedProducts[index].taxAmount = _taxTot;
                                updatedProducts[index].discount = _disTot.toFixed(2);
                                updatedProducts[index].total = (_netTot - _disTot + _taxTot).toFixed(2);
                                setAddedProducts(updatedProducts);

                                //update patch object
                                const _patchProducts = [...(objTobePatched?.products ?? [])];
                                const _svc = _patchProducts.find(
                                  (s) => (s.product!.id ?? "") == updatedProducts[index]!.product!.value
                                );
                                if (_svc) {
                                  _svc.price = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                  _svc.unitPrice = _svc.price / (_svc.quantity ?? 1);
                                  _svc.discountAmount = _disTot;
                                } else {
                                  _patchProducts.push({
                                    product: {
                                      id: parseFloat(updatedProducts[index]?.product.value ?? "0"),
                                      name: updatedProducts[index]?.product?.label,
                                    },
                                    quantity: updatedProducts[index].qty,
                                    price: parseFloat(updatedProducts[index]?.price ?? 0.0),
                                    unitPrice:
                                      parseFloat(updatedProducts[index]?.price ?? 0.0) / updatedProducts[index].qty,
                                    discountAmount: parseFloat(updatedProducts[index]?.discount ?? 0.0),
                                  });
                                }
                                setobjTobePatched({
                                  ...objTobePatched,
                                  products: _patchProducts,
                                });
                              }}
                              className="border rounded p-1 text-right w-full"
                            />
                            {/* <label htmlFor={`price_tax_${index}`} className="inline-block m-4 text-base font-medium">
                              Tax : {product.taxAmount.toFixed(2)}
                            </label> */}
                          </div>

                          <div className="lg:col-span-2 flex flex-row">
                            <label htmlFor={`p_discount_${index}`} className="inline-block m-4 text-sm w-[40%]">
                              Discount
                            </label>
                            <input
                              name={`p_discount_${index}`}
                              type="text"
                              placeholder="Discount"
                              value={product.discount}
                              disabled={(product.discountPercentage ?? 0) != 0}
                              onChange={(e) => {
                                if ((product.discountPercentage ?? 0) == 0) {
                                  const updatedProducts = [...addedProducts];
                                  updatedProducts[index].discount = (
                                    e.target.value === "" ? 0.0 : parseFloat(e.target.value)
                                  )
                                    //  .toFixed(2)
                                    .toString();
                                  let _netTot = parseFloat(updatedProducts[index].price);
                                  let _disTot = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                  let _taxTot = (_netTot - _disTot) * (updatedProducts[index].taxRate / 100);
                                  updatedProducts[index].taxAmount = _taxTot;
                                  // (updatedServices[index].discount = _disTot.toFixed(2).toString()),
                                  (updatedProducts[index].total = (_netTot + _taxTot - _disTot).toFixed(2).toString()),
                                    setAddedProducts(updatedProducts);
                                  //update patch object
                                  const _patchProducts = [...(objTobePatched?.products ?? [])];
                                  const _svc = _patchProducts.find(
                                    (s) => (s.product!.id ?? "") == updatedProducts[index]!.product!.value
                                  );
                                  if (_svc) {
                                    _svc.discountAmount = e.target.value === "" ? 0.0 : parseFloat(e.target.value);
                                  } else {
                                    _patchProducts.push({
                                      product: {
                                        id: parseFloat(updatedProducts[index]?.product.value ?? "0"),
                                        name: updatedProducts[index]?.product?.label,
                                      },
                                      quantity: updatedProducts[index].qty,
                                      price: parseFloat(updatedProducts[index]?.price ?? 0.0),
                                      discountAmount: parseFloat(updatedProducts[index]?.discount ?? 0.0),
                                    });
                                  }
                                  setobjTobePatched({
                                    ...objTobePatched,
                                    products: _patchProducts,
                                  });
                                }
                              }}
                              className="border rounded p-1 text-sm text-right w-full"
                            />
                          </div>
                          <div className="lg:col-span-2">
                            <label htmlFor={`p_price_total_${index}`} className="inline-block m-4 text-sm">
                              Total : {product.total ?? "0.00"}
                            </label>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleRemoveProduct(index)}
                              className="text-red-500 md:col-span-1"
                            >
                              <div className="flex flex-row">
                                <Trash2 />
                                {/*  <span className="p-1">Remove</span> */}
                              </div>
                            </button>
                          </div>
                          {/*  Line separator visible only on md and smaller screens  For large screens and larger ( lg, xl), the line separator will be hidden. */}
                          {addedProducts.length - 1 != index && (
                            <div className="lg:hidden border-t border-gray-300 my-4"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-5 font-bold">
                    Total Products/Parts Price: {AppUtility.FormattedPrice(totalProductPrice)}
                  </div>

                  <div className="grid grid-cols-[2fr_1fr] gap-4 mt-7">
                    <div className="flex justify-end font-bold text-lg">Sub Total:</div>
                    <div className="flex justify-end font-bold text-lg ">{AppUtility.FormattedPrice(subTotal)}</div>

                    <div className="flex justify-end font-bold text-lg">Total Discount:</div>
                    <div className="flex justify-end font-bold text-lg">{AppUtility.FormattedPrice(totalDiscount)}</div>

                    <div className="flex justify-end font-bold text-lg">Total Promo:</div>
                    <div className="flex justify-end font-bold text-lg">{AppUtility.FormattedPrice(totalPromo)}</div>
                    <div className="flex justify-end font-bold text-lg">Total:</div>
                    <div className="flex justify-end font-bold text-lg">{AppUtility.FormattedPrice(totalFee)}</div>
                  </div>

                  {(invoices ?? []).length > 0 && (
                    <div className="rounded-lg border border-gray-300 p-4 shadow-md">
                      <div className="grid grid-cols-1 gap-x-5">
                        <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                          <div className="xl:col-span-12">
                            <label htmlFor="service" className="inline-block mb-4 text-base font-bold">
                              Invoices
                            </label>
                          </div>
                          <div className="xl:col-span-10"></div>
                        </div>
                      </div>
                      {/* Invoices Section */}
                      <div className="grid grid-cols-1 gap-x-1 mt-4 text-sm font-medium">
                        {(invoices ?? []).map((inv: Invoice, index: number) => (
                          <div
                            key={`inv_${index}`}
                            className="grid grid-cols-1 md:grid-cols-12  gap-2 mb-2 items-center"
                          >
                            <span className="md:col-span-2">{inv.invoiceReference}</span>
                            <div className="md:col-span-1">
                              {(inv.isCancelled ?? false) && (
                                <div
                                  className={` inline-block px-4 py-1 rounded-lg text-sm text-white font-semibold bg-red-200`}
                                >
                                  <span>Cancelled</span>
                                </div>
                              )}
                            </div>
                            <div className="md:col-span-4 text-right">{(inv.totalAmount ?? 0).toFixed(2)}</div>
                            <div className="md:col-span-1"></div>
                            <div className="md:col-span-2 text-right">
                              {(inv.invoiceLines ?? []).length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    onResetInvoice(inv.id ?? "");
                                  }}
                                  className="text-blue-500"
                                >
                                  <div className="flex flex-row">
                                    <BookMinus />
                                    <span className="p-1">Reset Invoice</span>
                                  </div>
                                </button>
                              )}
                            </div>
                            <div className="md:col-span-2 text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  onViewInvoice(inv.id ?? "");
                                }}
                                className="text-blue-500"
                              >
                                <div className="flex flex-row">
                                  <View />
                                  <span className="p-1">View</span>
                                </div>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-300 p-4 shadow-md mt-8">
                    <div className="grid grid-cols-1 gap-x-5">
                      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
                        <div className="xl:col-span-12">
                          <label htmlFor="JobAssignment" className="inline-block mb-4 text-base font-bold">
                            Job Assignment
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-x-1 mt-4 text-sm font-medium">
                      {addedServices.map((service, index) => {
                        let _existing = jobAssignments.find((a) => a.serviceId == (service.service?.value ?? ""));
                        if (_existing == null) {
                          _existing = (exitsingAllocations ?? []).find(
                            (s) => s.serviceId == (service.service?.value ?? "")
                          );
                        }
                        //  console.log(new Date());
                        // console.log(format(new Date(), "yyyy-MM-dd", { timeZone }));
                        return (
                          <div
                            key={`assignment${index}`}
                            className="grid grid-cols-1 lg:grid-cols-12  gap-2 mb-2 items-center"
                          >
                            <span className="lg:col-span-4">{service.service?.label ?? ""}</span>
                            <div className="lg:col-span-4 flex flex-row">
                              <Select
                                className="border-slate-200 focus:outline-none focus:border-custom-500 w-full"
                                options={technicians}
                                isSearchable={true} // To disable search
                                name={`technician${index}`}
                                id={`technician${index}`}
                                value={technicians.find((t) => t.value == (_existing?.assignedUserId ?? ""))}
                                onChange={(option: SingleValue<LabelValuePair>) => {
                                  //console.log(option);
                                  const _dt =
                                    _existing != null
                                      ? AppUtility.ISOStringToDate(_existing.jobScheduleStart ?? "")
                                      : new Date();
                                  _dt.setHours(0);
                                  _dt.setMinutes(0);
                                  _dt.setSeconds(0);

                                  const _obj = jobAssignments.find(
                                    (a) => a.serviceId == (service.service?.value ?? "")
                                  );
                                  if (_obj != null) {
                                    _obj.assignedUserId = option?.value ?? "";
                                  } else {
                                    jobAssignments.push({
                                      serviceId: service.service?.value ?? "",
                                      assignedUserId: option?.value ?? "",
                                      allocationType: JobAllocationType.JOB,
                                      jobScheduleStart: AppUtility.ISODateToISOString(_dt),
                                    });
                                  }
                                  setJobAssignments([...(jobAssignments ?? [])]);
                                }}
                              />
                            </div>
                            <div className="lg:col-span-4 flex flex-row">
                              <div>
                                <Flatpickr
                                  id={`scheduleStart${index}`}
                                  className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500`}
                                  options={{
                                    dateFormat: "Y-m-d",
                                  }}
                                  placeholder="Schedule Date"
                                  value={format(
                                    _existing != null
                                      ? AppUtility.ISOStringToDate(_existing.jobScheduleStart ?? "")
                                      : new Date(),
                                    "yyyy-MM-dd",
                                    { timeZone }
                                  )}
                                  onChange={(date) => {
                                    const _dt = date[0];
                                    //const _dt = new Date(format(date[0], "yyyy-MM-dd", { timeZone }));
                                    _dt.setHours(0);
                                    _dt.setMinutes(0);
                                    _dt.setSeconds(0);
                                    console.log(_dt);

                                    const _obj = jobAssignments.find(
                                      (a) => a.serviceId == (service.service?.value ?? "")
                                    );
                                    if (_obj != null) {
                                      _obj.jobScheduleStart = AppUtility.ISODateToISOString(_dt);
                                    } else {
                                      jobAssignments.push({
                                        serviceId: service.service?.value ?? "",
                                        assignedUserId: _existing?.assignedUserId ?? "",
                                        allocationType: JobAllocationType.JOB,
                                        jobScheduleStart: AppUtility.ISODateToISOString(_dt),
                                      });
                                    }
                                    setJobAssignments([...(jobAssignments ?? [])]);
                                  }}
                                />
                              </div>
                            </div>

                            {/*  Line separator visible only on md and smaller screens  For large screens and larger ( lg, xl), the line separator will be hidden. */}
                            {addedServices.length - 1 != index && (
                              <div className="lg:hidden border-t border-gray-300 my-4"></div>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex justify-end gap-2 mt-5 font-bold">
                        <button
                          type="button"
                          className={`text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 
                            focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                            active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                            dark:ring-custom-400/20 px-8
                            ${jobAssignments.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                          disabled={jobAssignments.length == 0}
                          onClick={onAssignClick}
                        >
                          Assign
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between gap-2 mt-5">
                    <div className="flex justify-start gap-2 mt-5">
                      <button
                        type="button"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white 
                      hover:bg-custom-600 hover:border-custom-600 
                      focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 
                      active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 
                      dark:ring-custom-400/20
                      px-8
                      "
                        onClick={() => {
                          navigate(`${RoutePath.Admin_Job_list}`);
                        }}
                      >
                        Back
                      </button>
                    </div>
                    <div className="flex justify-end gap-2 mt-5">
                      <button
                        type="button"
                        onClick={handleResetClick}
                        className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
                      >
                        <X className="inline-block size-4" />
                        <span className="align-middle">Cancel</span>
                      </button>
                      <button
                        type="submit"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                      >
                        {(selected?.id ?? "") == "" ? "Create" : "Update"}
                      </button>
                    </div>
                  </div>
                </>
              </form>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default JobEditPage;
